import { useEffect, useRef, useState } from 'react';
import { resendVerification } from '../api/auth';
import { useAuth } from '../hooks/useAuth';

export default function VerifyEmailBanner() {
    const { user } = useAuth();
    const [sent, setSent] = useState(false);
    const bannerRef = useRef(null);

    // Existing accounts were deliberately left unverified rather than
    // backfilled, so this banner is the only path that verifies them.
    const hidden = !user || user.is_verified_email;

    // The banner is fixed under the header, so it covers the top of the page
    // unless the content below is pushed down by exactly its height. Height is
    // measured rather than hardcoded because the text wraps to a second line on
    // narrow screens, and it changes again once the confirmation replaces the
    // button. Declared before the early return so hook order stays stable.
    useEffect(() => {
        if (hidden) return undefined;
        const el = bannerRef.current;
        if (!el) return undefined;

        const root = document.documentElement;
        const applyHeight = () =>
            root.style.setProperty('--verify-banner-height', `${el.offsetHeight}px`);

        applyHeight();
        document.body.classList.add('has-verify-banner');

        // Guarded: jsdom has no ResizeObserver, and a missing one only costs us
        // re-measurement on resize, not the initial offset.
        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(applyHeight);
            observer.observe(el);
        }

        return () => {
            if (observer) observer.disconnect();
            document.body.classList.remove('has-verify-banner');
            root.style.removeProperty('--verify-banner-height');
        };
    }, [hidden]);

    if (hidden) return null;

    const handleResend = async () => {
        try {
            await resendVerification();
        } catch (error) {
            // The endpoint answers 200 whether or not it sent, so the only
            // failures here are transport-level. Showing the same confirmation
            // keeps the banner from becoming a retry loop.
        }
        setSent(true);
    };

    return (
        <div className="verify-email-banner" role="status" ref={bannerRef}>
            {sent ? (
                <span>Confirmation email sent. Please check your inbox.</span>
            ) : (
                <>
                    <span>Please confirm your email address.</span>
                    <button type="button" onClick={handleResend}>
                        Resend
                    </button>
                </>
            )}
        </div>
    );
}
